import { useRef } from 'react';
import { Printer, X, Download } from 'lucide-react';
import { Button } from '@lms/ui';
import type { ProgramRegistration, User, Program, ParticipantId } from '@lms/shared/types';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from './Bilingual';
import { copy } from '../copy/copy';

interface ReceiptModalProps {
  receiptData: ProgramRegistration & {
    user: User;
    program: Program;
    participantId?: ParticipantId;
    receipts?: { amount: number; paymentMethod: string }[];
  };
  onClose: () => void;
}

export function ReceiptModal({ receiptData, onClose }: ReceiptModalProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`<html><head><title>${t(copy.receiptModal.title)}</title>`);
    // Add tailwind or simple styles for printing
    printWindow.document.write('<style>');
    printWindow.document.write(`
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap');
      body { font-family: 'Inter', sans-serif; color: #1a1c1e; padding: 40px; }
      .receipt-card { border: 2px solid #f1f0f4; border-radius: 24px; padding: 40px; max-width: 800px; margin: 0 auto; }
      .header { display: flex; justify-content: space-between; align-items: start; border-bottom: 2px solid #f1f0f4; padding-bottom: 30px; margin-bottom: 30px; }
      .logo { font-weight: 900; font-size: 24px; letter-spacing: -0.05em; color: #0061a4; }
      .title { font-weight: 900; font-size: 32px; margin: 0; }
      .status { display: inline-block; padding: 6px 12px; background: #d7e3f4; color: #001d36; border-radius: 8px; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 40px; }
      .label { font-size: 10px; font-weight: 800; color: #43474e; text-transform: uppercase; letter-spacing: 0.15em; margin-bottom: 8px; }
      .value { font-size: 16px; font-weight: 700; margin: 0; }
      .participant-code { font-family: monospace; font-size: 24px; letter-spacing: 0.2em; color: #0061a4; background: #f1f0f4; padding: 20px; border-radius: 16px; text-align: center; margin: 40px 0; border: 2px dashed #0061a4; }
      .footer { font-size: 12px; color: #74777f; text-align: center; border-top: 1px solid #f1f0f4; pt: 20px; }
    `);
    printWindow.document.write('</style></head><body>');
    printWindow.document.write(printContent.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-50 flex justify-center p-4 animate-fadeIn overflow-y-auto py-12">
      <div className="bg-surface-container-lowest rounded-[2rem] w-full max-w-2xl shadow-level4 overflow-hidden animate-slideUp border border-on-surface/5 flex flex-col max-h-[90vh]">
        <div className="p-8 border-b border-outline-variant/10 bg-surface-container-low/30 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-2xl font-headline font-bold text-on-surface m-0 tracking-tight">
              <Bilingual {...copy.receiptModal.title} />
            </h2>
            <p className="text-xs font-body text-on-surface-variant mt-1 opacity-60 uppercase tracking-widest font-bold">
              <Bilingual {...copy.receiptModal.subtitle} />
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-on-surface/10 rounded-full transition-colors"
          >
            <X size={20} className="text-on-surface-variant" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-10 bg-surface-container-low/20">
          <div
            ref={printRef}
            className="receipt-card bg-white border-2 border-outline-variant/10 rounded-[2.5rem] p-12 shadow-sm"
          >
            <div className="flex justify-between items-start border-b border-outline-variant/10 pb-8 mb-10">
              <div className="space-y-4">
                <div className="text-primary font-black text-2xl tracking-tighter">LMS BID V3</div>
                <div className="px-3 py-1 bg-secondary-container text-on-secondary-container rounded-lg text-[10px] font-black uppercase tracking-widest inline-block">
                  {receiptData.status}
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest m-0">
                  <Bilingual {...copy.receiptModal.generatedOn} />
                </p>
                <p className="text-sm font-bold text-on-surface">
                  {new Date().toLocaleDateString('id-ID', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-10">
              <div className="space-y-1.5">
                <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60 m-0">
                  <Bilingual {...copy.receiptModal.participantIdentity} />
                </p>
                <p className="text-lg font-bold text-on-surface m-0">{receiptData.user.name}</p>
                <p className="text-xs font-mono text-on-surface-variant m-0">
                  {receiptData.user.email}
                </p>
              </div>
              <div className="space-y-1.5 text-right">
                <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60 m-0">
                  <Bilingual {...copy.receiptModal.instructionalTrack} />
                </p>
                <p className="text-lg font-bold text-on-surface m-0">{receiptData.program.name}</p>
                <p className="text-xs font-body text-on-surface-variant m-0">
                  {t(copy.receiptModal.code)}: {receiptData.program.code}
                </p>
              </div>
            </div>

            {/* Participation Metadata */}
            <div className="grid grid-cols-3 gap-6 py-8 border-y border-outline-variant/10 my-10">
              <div className="space-y-1">
                <p className="text-[8px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60 m-0">
                  <Bilingual {...copy.receiptModal.registration} />
                </p>
                <p className="text-xs font-bold text-on-surface">
                  {new Date(receiptData.registeredAt).toLocaleDateString('id-ID')}
                </p>
              </div>
              <div className="space-y-1 text-center">
                <p className="text-[8px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60 m-0">
                  <Bilingual {...copy.receiptModal.authorization} />
                </p>
                <p className="text-xs font-bold text-primary">
                  {receiptData.approvedAt
                    ? `${t(copy.receiptModal.verified)}: ${new Date(receiptData.approvedAt).toLocaleDateString('id-ID')}`
                    : t(copy.receiptModal.pendingApproval)}
                </p>
              </div>
              <div className="space-y-1 text-right">
                <p className="text-[8px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60 m-0">
                  <Bilingual {...copy.receiptModal.financialStatus} />
                </p>
                <p className="text-xs font-bold text-on-surface">
                  {receiptData.receipts && receiptData.receipts.length > 0
                    ? `${receiptData.receipts[0].paymentMethod} - ${new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(receiptData.receipts[0].amount)}`
                    : t(copy.receiptModal.freeNoRecord)}
                </p>
              </div>
            </div>

            <div className="mb-10 p-10 bg-primary/5 rounded-[2rem] border-2 border-dashed border-primary/20 flex flex-col items-center gap-2">
              <p className="text-[10px] font-label font-bold text-primary uppercase tracking-[0.25em] m-0">
                <Bilingual {...copy.receiptModal.officialParticipantCode} />
              </p>
              <div className="text-4xl font-headline font-black text-primary tracking-[0.2em] uppercase">
                {receiptData.participantId?.participantCode || t(copy.receiptModal.awaitingId)}
              </div>
            </div>

            <div className="mt-12 text-center">
              <p className="text-[10px] font-body text-on-surface-variant italic opacity-40 leading-relaxed">
                {t(copy.receiptModal.systemNotice)}
                <br />
                {t(copy.receiptModal.securityFingerprint)}: {receiptData.id}
              </p>
            </div>
          </div>
        </div>

        <div className="p-8 border-t border-outline-variant/10 bg-surface-container-low/30 flex justify-between gap-6 shrink-0">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-[10px] uppercase font-black tracking-widest"
          >
            {t(copy.receiptModal.btnClose)}
          </Button>
          <div className="flex gap-4">
            <Button
              variant="ghost"
              className="rounded-xl border border-outline-variant/30 text-[10px] uppercase font-black tracking-widest flex items-center gap-2"
            >
              <Download size={14} /> {t(copy.receiptModal.btnSave)}
            </Button>
            <Button
              onClick={handlePrint}
              className="rounded-xl px-10 text-[10px] uppercase font-black tracking-[0.25em] flex items-center gap-2 shadow-level2"
            >
              <Printer size={16} /> {t(copy.receiptModal.btnPrint)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
